import { collectAssistantCitations } from "@amoreg/shared/assistantCitations";
import { collectComposerInlineTokens } from "@amoreg/shared/composerInlineTokens";

export type PromptPreviewPart =
  | { kind: "text"; text: string }
  | { kind: "quote"; text: string; comment: string | undefined }
  | { kind: "reference"; label: string };

const words = new Intl.Segmenter(undefined, { granularity: "word" });

/** Count what the preview displays, not encoded citation addresses. */
export function promptPreviewWordCount(parts: readonly PromptPreviewPart[]): number {
  const text = parts
    .map((part) =>
      part.kind === "reference"
        ? part.label
        : part.kind === "quote"
          ? `${part.text} ${part.comment ?? ""}`
          : part.text,
    )
    .join("");
  return Array.from(words.segment(text)).filter((word) => word.isWordLike).length;
}

/** Render references for people while keeping the underlying draft completely untouched. */
export function promptAssistancePreviewParts(prompt: string): PromptPreviewPart[] {
  const ranges: { start: number; end: number; part: PromptPreviewPart }[] = [
    ...collectAssistantCitations(prompt).map(({ start, end, citation }) => ({
      start,
      end,
      part: { kind: "quote" as const, text: citation.text, comment: citation.comment },
    })),
    ...collectComposerInlineTokens(`${prompt} `).map((token) => ({
      start: token.start,
      end: token.end,
      part: {
        kind: "reference" as const,
        label:
          token.type === "skill"
            ? `$${token.value}`
            : token.type === "policy" || token.type === "connection"
              ? token.label
              : `@${token.value.split(/[\\/]/).at(-1) ?? token.value}`,
      },
    })),
    ...Array.from(prompt.matchAll(/\uFFFC/g), (match) => ({
      start: match.index,
      end: match.index + 1,
      part: { kind: "reference" as const, label: "Selected terminal output" },
    })),
  ];
  ranges.sort((a, b) => a.start - b.start || b.end - a.end);
  const parts: PromptPreviewPart[] = [];
  let cursor = 0;
  for (const { start, end, part } of ranges) {
    if (start < cursor) continue;
    if (start > cursor) parts.push({ kind: "text", text: prompt.slice(cursor, start) });
    parts.push(part);
    cursor = end;
  }
  if (cursor < prompt.length) parts.push({ kind: "text", text: prompt.slice(cursor) });
  return parts;
}
